import {
  AdInfoRow,
  CDN,
  CellData,
  ColumnMetaData,
  ColumnMetaDataMap,
  DimensionMap,
} from "../MetricsTable/metricsTableUtils";
import { Metric, NativeCurrencyMetric } from "@blisspointmedia/bpm-types/dist/CrossChannel";
import * as P from "@blisspointmedia/bpm-types/dist/Performance";
import * as R from "ramda";
import {
  DELIVERY_CURRENCY_CODE,
  METRIC_TO_PRETTY_NAME,
  PERFORMANCE_CURRENCY_CODE,
} from "../../CrossChannel/crossChannelConstants";
import {
  IMP_IN_THOUSANDS_FORMATTER,
  metricFormatter,
  metricFormatterGBP,
  showCAD,
  showGBP,
  SPEND_OR_REVENUE_FORMATTER,
  toPrettyNumber,
} from "../../utils/metric-format-utils";
import { DimensionColumn } from "@blisspointmedia/bpm-types/dist/MetricsTable";
import {
  DimensionColNames,
  getDataColForMetadata,
  getDimensionColForMetadata,
} from "@blisspointmedia/bpm-types/dist/Columns";
import { App2Channels } from "@blisspointmedia/bpm-types/dist/ScpChannels";

const {
  CLICKS,
  IMPRESSIONS,
  VOLUME,
  SPEND,
  REVENUE,
  CPC,
  CPM,
  ROAS,
  CPX,
  CTR,
  CLICKS_CONVERSION_RATE,
  IMPS_CONVERSION_RATE,
  AOV,
} = Metric;

const {
  CPC_NATIVE,
  CPM_NATIVE,
  CPX_NATIVE,
  SPEND_NATIVE,
  REVENUE_NATIVE,
  AOV_NATIVE,
} = NativeCurrencyMetric;

// when possible we prefer decimels are built into formatter
// so that metricFormatter() may be called outside of metrics table
// but may be included to pass to formatValue later

export const sharedDimensionColumnMetaDataMap: Partial<Record<string, ColumnMetaData>> = {
  Date: {
    contentType: "text",
    dimensionVarName: "Date",
    displayName: "Date",
  },
};

export const sharedColumnMetaDataMap: Partial<Record<P.ColumnType, ColumnMetaData>> = {
  [CLICKS]: {
    displayName: METRIC_TO_PRETTY_NAME[CLICKS],
    formatValue: metricFormatter(CLICKS),
  },
  [SPEND]: {
    displayName: METRIC_TO_PRETTY_NAME[SPEND],
    formatValue: metricFormatter(SPEND),
  },
  [IMPRESSIONS]: {
    displayName: "Impressions (000s)",
    formatValue: IMP_IN_THOUSANDS_FORMATTER,
  },
  [VOLUME]: {
    displayName: METRIC_TO_PRETTY_NAME[VOLUME],
    formatValue: metricFormatter(VOLUME),
  },
  [REVENUE]: {
    displayName: METRIC_TO_PRETTY_NAME[REVENUE],
    formatValue: metricFormatter(REVENUE),
  },
  [CPC]: {
    displayName: METRIC_TO_PRETTY_NAME[CPC],
    formatValue: metricFormatter(CPC),
    aggregator: agg =>
      agg[CLICKS] && agg[SPEND] ? (agg[SPEND] as number) / (agg[CLICKS] as number) : "--",
    fetchGetter: fetch =>
      fetch[CLICKS] && fetch[SPEND] ? (fetch[SPEND] as number) / (fetch[CLICKS] as number) : "--",
    requiredTotalsColumns: [CLICKS, SPEND],
  },
  [CPM]: {
    displayName: METRIC_TO_PRETTY_NAME[CPM],
    formatValue: metricFormatter(CPM),
    aggregator: agg =>
      agg[IMPRESSIONS] && agg[SPEND]
        ? ((agg[SPEND] as number) * 1000) / (agg[IMPRESSIONS] as number)
        : "--",
    fetchGetter: fetch =>
      fetch[IMPRESSIONS] && fetch[SPEND]
        ? ((fetch[SPEND] as number) * 1000) / (fetch[IMPRESSIONS] as number)
        : "--",
    requiredTotalsColumns: [IMPRESSIONS, SPEND],
  },
  [ROAS]: {
    displayName: METRIC_TO_PRETTY_NAME[ROAS],
    formatValue: metricFormatter(ROAS),
    aggregator: agg =>
      agg[REVENUE] && agg[SPEND] ? (agg[REVENUE] as number) / (agg[SPEND] as number) : "--",
    fetchGetter: fetch =>
      fetch[REVENUE] && fetch[SPEND] ? (fetch[REVENUE] as number) / (fetch[SPEND] as number) : 0,
    requiredTotalsColumns: [REVENUE, SPEND],
  },
  [CPX]: {
    displayName: METRIC_TO_PRETTY_NAME[CPX],
    formatValue: metricFormatter(CPX),
    aggregator: agg =>
      agg[VOLUME] && agg[SPEND] ? (agg[SPEND] as number) / (agg[VOLUME] as number) : "--",
    fetchGetter: fetch =>
      fetch[VOLUME] && fetch[SPEND] ? (fetch[SPEND] as number) / (fetch[VOLUME] as number) : "--",
    requiredTotalsColumns: [VOLUME, SPEND],
  },
  [CTR]: {
    displayName: METRIC_TO_PRETTY_NAME[CTR],
    formatValue: metricFormatter(CTR),
    aggregator: agg =>
      agg[CLICKS] && agg[IMPRESSIONS]
        ? (agg[CLICKS] as number) / (agg[IMPRESSIONS] as number)
        : "--",
    fetchGetter: fetch =>
      fetch[CLICKS] && fetch[IMPRESSIONS]
        ? (fetch[CLICKS] as number) / (fetch[IMPRESSIONS] as number)
        : "--",
    requiredTotalsColumns: [CLICKS, IMPRESSIONS],
  },
  [CLICKS_CONVERSION_RATE]: {
    displayName: METRIC_TO_PRETTY_NAME[CLICKS_CONVERSION_RATE],
    formatValue: metricFormatter(CLICKS_CONVERSION_RATE),
    aggregator: agg =>
      agg[VOLUME] && agg[CLICKS] ? (agg[VOLUME] as number) / (agg[CLICKS] as number) : "--",
    fetchGetter: fetch =>
      fetch[VOLUME] && fetch[CLICKS] ? (fetch[VOLUME] as number) / (fetch[CLICKS] as number) : "--",
    requiredTotalsColumns: [VOLUME, CLICKS],
  },
  [IMPS_CONVERSION_RATE]: {
    displayName: METRIC_TO_PRETTY_NAME[IMPS_CONVERSION_RATE],
    formatValue: metricFormatter(IMPS_CONVERSION_RATE),
    aggregator: agg =>
      agg[VOLUME] && agg[IMPRESSIONS]
        ? (agg[VOLUME] as number) / (agg[IMPRESSIONS] as number)
        : "--",
    fetchGetter: fetch =>
      fetch[VOLUME] && fetch[IMPRESSIONS]
        ? (fetch[VOLUME] as number) / (fetch[IMPRESSIONS] as number)
        : "--",
    requiredTotalsColumns: [VOLUME, IMPRESSIONS],
  },
  [SPEND_NATIVE]: {
    displayName: METRIC_TO_PRETTY_NAME[SPEND_NATIVE],
    formatValue: metricFormatter(SPEND_NATIVE),
    currencyGetter: col =>
      col[DELIVERY_CURRENCY_CODE] === "" ? "USD" : (col[DELIVERY_CURRENCY_CODE] as string),
    requiredTotalsColumns: [DELIVERY_CURRENCY_CODE, SPEND_NATIVE],
  },
  [REVENUE_NATIVE]: {
    displayName: METRIC_TO_PRETTY_NAME[REVENUE_NATIVE],
    formatValue: metricFormatter(REVENUE_NATIVE),
    currencyGetter: col =>
      col[PERFORMANCE_CURRENCY_CODE] === "" ? "USD" : (col[PERFORMANCE_CURRENCY_CODE] as string),
    requiredTotalsColumns: [PERFORMANCE_CURRENCY_CODE, REVENUE_NATIVE],
  },
  [CPC_NATIVE]: {
    displayName: METRIC_TO_PRETTY_NAME[CPC_NATIVE],
    formatValue: metricFormatter(CPC_NATIVE),
    aggregator: agg =>
      agg[CLICKS] && agg[SPEND_NATIVE]
        ? (agg[SPEND_NATIVE] as number) / (agg[CLICKS] as number)
        : "--",
    fetchGetter: fetch =>
      fetch[CLICKS] && fetch[SPEND_NATIVE]
        ? (fetch[SPEND_NATIVE] as number) / (fetch[CLICKS] as number)
        : "--",
    requiredTotalsColumns: [CLICKS, SPEND_NATIVE, DELIVERY_CURRENCY_CODE],
    currencyGetter: col =>
      col[DELIVERY_CURRENCY_CODE] === "" ? "USD" : (col[DELIVERY_CURRENCY_CODE] as string),
  },
  [CPM_NATIVE]: {
    displayName: METRIC_TO_PRETTY_NAME[CPM_NATIVE],
    formatValue: metricFormatter(CPM_NATIVE),
    aggregator: agg =>
      agg[IMPRESSIONS] && agg[SPEND_NATIVE]
        ? ((agg[SPEND_NATIVE] as number) * 1000) / (agg[IMPRESSIONS] as number)
        : "--",
    fetchGetter: fetch =>
      fetch[IMPRESSIONS] && fetch[SPEND_NATIVE]
        ? ((fetch[SPEND_NATIVE] as number) * 1000) / (fetch[IMPRESSIONS] as number)
        : "--",
    requiredTotalsColumns: [IMPRESSIONS, SPEND_NATIVE, DELIVERY_CURRENCY_CODE],
    currencyGetter: col =>
      col[DELIVERY_CURRENCY_CODE] === "" ? "USD" : (col[DELIVERY_CURRENCY_CODE] as string),
  },
  [CPX_NATIVE]: {
    displayName: METRIC_TO_PRETTY_NAME[CPX_NATIVE],
    formatValue: metricFormatter(CPX_NATIVE),
    aggregator: agg =>
      agg[VOLUME] && agg[SPEND_NATIVE]
        ? (agg[SPEND_NATIVE] as number) / (agg[VOLUME] as number)
        : "--",
    fetchGetter: fetch =>
      fetch[VOLUME] && fetch[SPEND_NATIVE]
        ? (fetch[SPEND_NATIVE] as number) / (fetch[VOLUME] as number)
        : "--",
    requiredTotalsColumns: [VOLUME, SPEND_NATIVE, DELIVERY_CURRENCY_CODE],
    currencyGetter: col =>
      col[DELIVERY_CURRENCY_CODE] === "" ? "USD" : (col[DELIVERY_CURRENCY_CODE] as string),
  },
  [AOV_NATIVE]: {
    aggregator: (agg => {
      const volume = agg.conversions || agg.volume;
      return volume && (volume as number) > 0 && agg[REVENUE_NATIVE]
        ? (agg[REVENUE_NATIVE] as number) / R.defaultTo(0.0, volume as number)
        : "--";
    }) as (agg: Record<string, string | number>) => number | string,
    displayName: "AOV Raw",
    fetchGetter: (fetch => {
      const volume = fetch.conversions || fetch.volume;
      return volume && (volume as number) > 0 && fetch[REVENUE_NATIVE]
        ? (fetch[REVENUE_NATIVE] as number) / R.defaultTo(0.0, volume as number)
        : "--";
    }) as (
      fetch: Record<string, string | number>,
      aggregatedData: Record<string, any>
    ) => number | string,
    formatValue: SPEND_OR_REVENUE_FORMATTER,
    currencyGetter: col =>
      col[PERFORMANCE_CURRENCY_CODE] === "" ? "USD" : (col[PERFORMANCE_CURRENCY_CODE] as string),
    requiredTotalsColumns: [PERFORMANCE_CURRENCY_CODE, REVENUE_NATIVE, "conversions", "volume"],
  },
  [AOV]: {
    aggregator: (agg => {
      const volume = agg.conversions || agg.volume;
      return volume && (volume as number) > 0 && agg.revenue
        ? (agg.revenue as number) / R.defaultTo(0.0, volume as number)
        : "--";
    }) as (agg: Record<string, string | number>) => number | string,
    displayName: "AOV",
    fetchGetter: (fetch => {
      const volume = fetch.conversions || fetch.volume;
      return volume && (volume as number) > 0 && fetch.revenue
        ? (fetch.revenue as number) / R.defaultTo(0.0, volume as number)
        : "--";
    }) as (
      fetch: Record<string, string | number>,
      aggregatedData: Record<string, any>
    ) => number | string,
    formatValue: SPEND_OR_REVENUE_FORMATTER,
    requiredTotalsColumns: ["revenue", "conversions", "volume"],
  },
  video_views: {
    displayName: "Video Views",
    formatValue: toPrettyNumber,
  },
  videos_watched_at_25p: {
    displayName: "Videos Watched at 25%",
    formatValue: toPrettyNumber,
  },
  videos_watched_at_50p: {
    displayName: "Videos Watched at 50%",
    formatValue: toPrettyNumber,
  },
  videos_watched_at_75p: {
    displayName: "Videos Watched at 75%",
    formatValue: toPrettyNumber,
  },
  videos_watched_at_100p: {
    displayName: "Videos Watched at 100%",
    formatValue: toPrettyNumber,
  },
  acos: {
    aggregator: agg =>
      agg.spend && (agg.spend as number) > 0 && agg.revenue
        ? Number(agg.spend as number) / R.defaultTo(0.0, agg.revenue as number)
        : "--",
    displayName: "ACOS",
    fetchGetter: fetch =>
      fetch.spend && (fetch.spend as number) > 0 && fetch.revenue
        ? Number(fetch.spend as number) / R.defaultTo(0.0, fetch.revenue as number)
        : "--",
    formatValue: metricFormatter("acos"),
    requiredTotalsColumns: ["revenue", "spend"],
  },
  deliveries: {
    displayName: "Deliveries",
    formatValue: metricFormatter(CLICKS),
  },
  sends: {
    displayName: "Sends",
    formatValue: metricFormatter(CLICKS),
  },
  opens: {
    displayName: "Opens",
    formatValue: metricFormatter(CLICKS),
  },
  unsubscribes: {
    displayName: "Unsubscribes",
    formatValue: metricFormatter(CLICKS),
  },
  bounces: {
    displayName: "Bounces",
    formatValue: metricFormatter(CLICKS),
  },
  spam_abuse_reports: {
    displayName: "Spam Reports",
    formatValue: metricFormatter(CLICKS),
  },
  ctrEmail: {
    displayName: METRIC_TO_PRETTY_NAME.ctrEmail,
    formatValue: metricFormatter("ctrEmail"),
    aggregator: agg =>
      agg[CLICKS] && agg.deliveries ? (agg[CLICKS] as number) / (agg.deliveries as number) : "--",
    fetchGetter: fetch =>
      fetch[CLICKS] && fetch.deliveries
        ? (fetch[CLICKS] as number) / (fetch.deliveries as number)
        : "--",
    requiredTotalsColumns: [CLICKS, "deliveries"],
  },
  ctor: {
    displayName: "CTOR",
    formatValue: metricFormatter(CTR),
    aggregator: agg =>
      agg[CLICKS] && agg.opens ? (agg[CLICKS] as number) / (agg.opens as number) : "--",
    fetchGetter: fetch =>
      fetch[CLICKS] && fetch.opens ? (fetch[CLICKS] as number) / (fetch.opens as number) : "--",
    requiredTotalsColumns: [CLICKS, "opens"],
  },
  revenuePerDelivered: {
    displayName: "Revenue per Email",
    formatValue: metricFormatter("revenuePerDelivered"),
    aggregator: agg =>
      agg.revenue && agg.deliveries ? (agg.revenue as number) / (agg.deliveries as number) : "--",
    fetchGetter: fetch =>
      fetch.revenue && fetch.deliveries
        ? (fetch.revenue as number) / (fetch.deliveries as number)
        : "--",
    requiredTotalsColumns: ["revenue", "deliveries"],
  },
  revenuePerDeliveredNative: {
    aggregator: agg =>
      agg[REVENUE_NATIVE] && agg.deliveries
        ? (agg[REVENUE_NATIVE] as number) / (agg.deliveries as number)
        : "--",
    displayName: "Revenue per Email Raw",
    fetchGetter: fetch =>
      fetch[REVENUE_NATIVE] && fetch.deliveries
        ? (fetch[REVENUE_NATIVE] as number) / (fetch.deliveries as number)
        : "--",
    formatValue: SPEND_OR_REVENUE_FORMATTER,
    currencyGetter: col =>
      col[PERFORMANCE_CURRENCY_CODE] === "" ? "USD" : (col[PERFORMANCE_CURRENCY_CODE] as string),
    requiredTotalsColumns: [PERFORMANCE_CURRENCY_CODE, REVENUE_NATIVE, "deliveries"],
  },
  bounceRate: {
    displayName: "Bounce Rate",
    formatValue: metricFormatter("bounceRate"),
    aggregator: agg =>
      agg.bounces && agg.deliveries ? (agg.bounces as number) / (agg.deliveries as number) : "--",
    fetchGetter: fetch =>
      fetch.bounces && fetch.deliveries
        ? (fetch.bounces as number) / (fetch.deliveries as number)
        : "--",
    requiredTotalsColumns: ["bounces", "deliveries"],
  },
  openRate: {
    displayName: "Open Rate",
    formatValue: metricFormatter("openRate"),
    aggregator: agg =>
      agg.opens && agg.deliveries ? (agg.opens as number) / (agg.deliveries as number) : "--",
    fetchGetter: fetch =>
      fetch.opens && fetch.deliveries
        ? (fetch.opens as number) / (fetch.deliveries as number)
        : "--",
    requiredTotalsColumns: ["opens", "deliveries"],
  },
  unsubscribeRate: {
    displayName: "Unsubscribe Rate",
    formatValue: metricFormatter("unsubscribeRate"),
    aggregator: agg =>
      agg.unsubscribes && agg.deliveries
        ? (agg.unsubscribes as number) / (agg.deliveries as number)
        : "--",
    fetchGetter: fetch =>
      fetch.unsubscribes && fetch.deliveries
        ? (fetch.unsubscribes as number) / (fetch.deliveries as number)
        : "--",
    requiredTotalsColumns: ["unsubscribes", "deliveries"],
  },
  clickToOpenRate: {
    displayName: "Click to Open Rate",
    formatValue: metricFormatter("clickToOpenRate"),
    aggregator: agg =>
      agg.clicks && agg.opens ? (agg.clicks as number) / (agg.opens as number) : "--",
    fetchGetter: fetch =>
      fetch.clicks && fetch.opens ? (fetch.clicks as number) / (fetch.opens as number) : "--",
    requiredTotalsColumns: ["clicks", "opens"],
  },
  spamReportRate: {
    displayName: "Spam Report Rate",
    formatValue: metricFormatter("spamReportRate"),
    aggregator: agg =>
      agg.spam_abuse_reports && agg.deliveries
        ? (agg.spam_abuse_reports as number) / (agg.deliveries as number)
        : "--",
    fetchGetter: fetch =>
      fetch.spam_abuse_reports && fetch.deliveries
        ? (fetch.spam_abuse_reports as number) / (fetch.deliveries as number)
        : "--",
    requiredTotalsColumns: ["deliveries", "spam_abuse_reports"],
  },
  // TODO remove once streamging / linear are migrated to standard SCP
  cost: {
    displayName: METRIC_TO_PRETTY_NAME[SPEND],
    formatValue: metricFormatter(SPEND),
  },
  count: {
    displayName: "Impressions",
    formatValue: metricFormatter(VOLUME),
  },
  eCpm: {
    displayName: "eCPM",
    formatValue: metricFormatter(CPM),
    aggregator: agg =>
      agg.count && agg.cost ? ((agg.cost as number) * 1000) / (agg.count as number) : "--",
    fetchGetter: fetch =>
      fetch.count && fetch.cost ? ((fetch.cost as number) * 1000) / (fetch.count as number) : "--",
    requiredTotalsColumns: ["cost", "count"],
  },
  youtubeCpm: {
    displayName: "CPM",
    formatValue: metricFormatter(CPM),
    aggregator: agg =>
      agg.count && agg.cost ? ((agg.cost as number) * 1000) / (agg.count as number) : "--",
    fetchGetter: fetch =>
      fetch.count && fetch.cost ? ((fetch.cost as number) * 1000) / (fetch.count as number) : "--",
    requiredTotalsColumns: ["cost", "count"],
  },
};

export const cadOverrideColumnMetaDataMap: Partial<Record<P.ColumnType, ColumnMetaData>> = {
  [SPEND]: {
    ...(sharedColumnMetaDataMap[SPEND] as ColumnMetaData),
    formatValue: metricFormatter(SPEND, true),
  },
  [REVENUE]: {
    ...(sharedColumnMetaDataMap[REVENUE] as ColumnMetaData),
    formatValue: metricFormatter(REVENUE, true),
  },
  [CPC]: {
    ...(sharedColumnMetaDataMap[CPC] as ColumnMetaData),
    formatValue: metricFormatter(CPC, true),
  },
  [CPM]: {
    ...(sharedColumnMetaDataMap[CPM] as ColumnMetaData),
    formatValue: metricFormatter(CPM, true),
  },
  [CPX]: {
    ...(sharedColumnMetaDataMap[CPX] as ColumnMetaData),
    formatValue: metricFormatter(CPX, true),
  },
  costPerConversion: {
    ...(sharedColumnMetaDataMap.costPerConversion as ColumnMetaData),
    formatValue: metricFormatter("costPerConversion", true),
  },
  revenuePerDelivered: {
    ...(sharedColumnMetaDataMap.revenuePerDelivered as ColumnMetaData),
    formatValue: metricFormatter("revenuePerDelivered", true),
  },
};

export const gbpOverrideColumnMetaDataMap: Partial<Record<P.ColumnType, ColumnMetaData>> = {
  [SPEND]: {
    ...(sharedColumnMetaDataMap[SPEND] as ColumnMetaData),
    formatValue: metricFormatterGBP(SPEND),
  },
  [REVENUE]: {
    ...(sharedColumnMetaDataMap[REVENUE] as ColumnMetaData),
    formatValue: metricFormatterGBP(REVENUE),
  },
  [CPC]: {
    ...(sharedColumnMetaDataMap[CPC] as ColumnMetaData),
    formatValue: metricFormatterGBP(CPC),
  },
  [CPM]: {
    ...(sharedColumnMetaDataMap[CPM] as ColumnMetaData),
    formatValue: metricFormatterGBP(CPM),
  },
  [CPX]: {
    ...(sharedColumnMetaDataMap[CPX] as ColumnMetaData),
    formatValue: metricFormatterGBP(CPX),
  },
  costPerConversion: {
    ...(sharedColumnMetaDataMap.costPerConversion as ColumnMetaData),
    formatValue: metricFormatterGBP("costPerConversion"),
  },
  revenuePerDelivered: {
    ...(sharedColumnMetaDataMap.revenuePerDelivered as ColumnMetaData),
    formatValue: metricFormatterGBP("revenuePerDelivered"),
  },
};

export const getCompanyChannelColumnMetaData = (
  columnMetaDataMap: Partial<Record<P.ColumnType, ColumnMetaData>>,
  company: string
): Partial<Record<P.ColumnType, ColumnMetaData>> => {
  if (showCAD(company)) {
    const cadMetaDataTarget = { ...columnMetaDataMap };
    Object.keys(cadMetaDataTarget).forEach(key => {
      if (cadOverrideColumnMetaDataMap.hasOwnProperty(key)) {
        cadMetaDataTarget[key] = cadOverrideColumnMetaDataMap[key];
      }
    });
    return cadMetaDataTarget;
  } else if (showGBP(company)) {
    const gbpMetaDataTarget = { ...columnMetaDataMap };
    Object.keys(gbpMetaDataTarget).forEach(key => {
      if (gbpOverrideColumnMetaDataMap.hasOwnProperty(key)) {
        gbpMetaDataTarget[key] = gbpOverrideColumnMetaDataMap[key];
      }
    });
    return gbpMetaDataTarget;
  }
  return columnMetaDataMap;
};
// Can replace all other versions of this function, TODO delete others
export const getDimensionCell = (
  dimensionData: DimensionMap,
  dimensionHeader: DimensionColumn,
  adInfoMap?: Record<string, AdInfoRow>
): CellData => {
  const { dimensionVarName, dimensionTypeName } = dimensionHeader;
  const resolvedDimensionData = dimensionData as Record<DimensionColNames, string>;
  const dimensionValue = resolvedDimensionData[dimensionVarName];
  const defaultCell = {
    value: dimensionValue,
    label: dimensionValue,
  };
  if (dimensionTypeName === "Platform") {
    const parsedDimensionValue = dimensionValue.replace(/\s+/g, "");
    const url = `${CDN}/assets/img/platforms/${parsedDimensionValue}.png`;
    return {
      label: dimensionValue,
      overlayText: dimensionValue,
      value: dimensionValue,
      url,
    };
  } else if (dimensionTypeName === "Retailer") {
    const parsedDimensionValue = dimensionValue.replace(/\s+/g, "");
    const url = `${CDN}/assets/img/retailers/${parsedDimensionValue}.png`;
    return {
      label: dimensionValue,
      overlayText: dimensionValue,
      value: dimensionValue,
      url,
    };
  } else if (dimensionTypeName === "Ad Name" && adInfoMap && adInfoMap[dimensionValue]) {
    const adInfo = adInfoMap[dimensionValue];
    const url = `${CDN}/creativeAssets/metaThumbnails/${adInfo.company}/${adInfo.account_id}/${adInfo.ad_id}.jpg`;
    return {
      label: dimensionValue,
      overlayText: dimensionValue,
      value: dimensionValue,
      url,
      adInfo,
    };
  } else if (dimensionTypeName === "Ad Name") {
    return {
      label: dimensionValue,
      overlayText: dimensionValue, // allows tooltip for longer ad names
      value: dimensionValue,
    };
  }
  return defaultCell;
};

export const getDataColMetaData = (channel: App2Channels, company: string): ColumnMetaDataMap => {
  const showCA = showCAD(company);
  const showGB = showGBP(company);
  const items = getDataColForMetadata(channel);
  const columnMetaDataMap = items.reduce((map, { name: metric }) => {
    if (sharedColumnMetaDataMap.hasOwnProperty(metric)) {
      if (showCA && cadOverrideColumnMetaDataMap.hasOwnProperty(metric)) {
        map[metric] = cadOverrideColumnMetaDataMap[metric];
      } else if (showGB && gbpOverrideColumnMetaDataMap.hasOwnProperty(metric)) {
        map[metric] = gbpOverrideColumnMetaDataMap[metric];
      } else {
        map[metric] = sharedColumnMetaDataMap[metric];
      }
    }
    return map;
  }, {});

  return columnMetaDataMap;
};

export const getDimensionColMetaData = (channel: App2Channels): ColumnMetaDataMap => {
  return getDimensionColForMetadata(channel).reduce(
    (map, col) => {
      map[col.prettyName] = {
        contentType: "text",
        dimensionVarName: col.name,
        displayName: col.prettyName,
        iconStyle: ["platform", "retailer"].includes(col.name) ? "logo"
        : col.name === "ad_name" ? "thumbnail"
        : undefined,
      };
      return map;
    },
    { ...sharedDimensionColumnMetaDataMap }
  );
};
